import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Repeat2 } from 'lucide-react';

const COLORS = ['#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6', '#4c1d95', '#3b0764'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1e1040] border border-white/20 rounded-xl p-3 shadow-xl text-sm">
      <p className="text-white font-semibold mb-1">{label}</p>
      <p className="text-violet-300">Total Rewatches: <span className="text-white font-mono">{payload?.[0]?.value}</span></p>
      <p className="text-amber-300">Rewatch Rate: <span className="text-white font-mono">{payload?.[1]?.value}%</span></p>
      <p className="text-white/50 text-xs mt-1">Avg per view: {payload?.[2]?.value}</p>
    </div>
  );
};

const RewatchPatternChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="h-5 w-48 bg-white/10 rounded animate-pulse mb-4" />
        <div className="h-56 bg-white/5 rounded-xl animate-pulse" />
      </div>
    );
  }

  const chartData = (data || [])?.map(d => ({
    name: d?.label,
    totalRewatches: d?.totalRewatches,
    rewatchRate: d?.rewatchRate,
    avgRewatches: d?.avgRewatches,
    totalViews: d?.totalViews,
  }));

  const maxRewatches = Math.max(...chartData?.map(d => d?.totalRewatches), 1);

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Repeat2 className="w-4 h-4 text-violet-400" />
        <h3 className="text-white font-semibold text-base">Rewatch Patterns by Level</h3>
      </div>
      <p className="text-white/40 text-xs mb-5">Which lesson levels get rewatched most</p>
      {chartData?.every(d => d?.totalRewatches === 0) ? (
        <div className="h-48 flex items-center justify-center">
          <p className="text-white/30 text-sm">No rewatch data for this period.</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={30}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="totalRewatches" radius={[6, 6, 0, 0]} name="Total Rewatches">
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`rgba(139, 92, 246, ${0.4 + (entry?.totalRewatches / maxRewatches) * 0.6})`}
                  />
                ))}
              </Bar>
              {/* Hidden bars for tooltip data */}
              <Bar dataKey="rewatchRate" hide name="Rewatch Rate" />
              <Bar dataKey="avgRewatches" hide name="Avg Rewatches" />
            </BarChart>
          </ResponsiveContainer>

          {/* Level stats row */}
          <div className="grid grid-cols-6 gap-2 mt-4">
            {chartData?.map((d, i) => (
              <div key={d?.name} className="text-center">
                <p className="text-xs text-white/50 font-medium">{d?.rewatchRate}%</p>
                <p className="text-[10px] text-white/25 mt-0.5">rewatch rate</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RewatchPatternChart;
